/**
 * Convex Hull Utilities
 *
 * Algorithms for generating convex collision shapes from sprite pixels.
 *
 * @module @engine/physics2d/ConvexHull
 */

// ============================================================================
// TYPES
// ============================================================================

export interface Point2D {
  x: number;
  y: number;
}

// ============================================================================
// CONVEX HULL — GRAHAM SCAN
// ============================================================================

/**
 * Cross product of vectors OA and OB where O is the origin.
 * Positive if counter-clockwise, negative if clockwise, zero if collinear.
 */
function cross(o: Point2D, a: Point2D, b: Point2D): number {
  return (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
}

/**
 * Compute the convex hull of a set of 2D points using the Monotone Chain algorithm.
 * Returns points in counter-clockwise order.
 *
 * @param points - Array of 2D points
 * @returns Convex hull vertices in CCW order
 */
export function computeConvexHull(points: Point2D[]): Point2D[] {
  if (points.length < 3) return [...points];

  // Sort points by x, then by y
  const sorted = [...points].sort((a, b) => a.x !== b.x ? a.x - b.x : a.y - b.y);

  // Build lower hull
  const lower: Point2D[] = [];
  for (const p of sorted) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) {
      lower.pop();
    }
    lower.push(p);
  }

  // Build upper hull
  const upper: Point2D[] = [];
  for (let i = sorted.length - 1; i >= 0; i--) {
    const p = sorted[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) {
      upper.pop();
    }
    upper.push(p);
  }

  // Remove last point of each half because it's repeated
  lower.pop();
  upper.pop();

  return lower.concat(upper);
}

// ============================================================================
// HULL SIMPLIFICATION
// ============================================================================

/**
 * Squared distance from point P to line segment AB.
 */
function pointToSegmentDistanceSq(p: Point2D, a: Point2D, b: Point2D): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lengthSq = dx * dx + dy * dy;

  if (lengthSq === 0) {
    // Segment is a point
    const ex = p.x - a.x;
    const ey = p.y - a.y;
    return ex * ex + ey * ey;
  }

  // Project point onto line, clamping to segment
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lengthSq;
  t = Math.max(0, Math.min(1, t));

  const projX = a.x + t * dx;
  const projY = a.y + t * dy;
  const ex = p.x - projX;
  const ey = p.y - projY;
  return ex * ex + ey * ey;
}

/**
 * Simplify a polygon using the Ramer-Douglas-Peucker algorithm.
 *
 * @param points - Polygon vertices (will be treated as closed)
 * @param epsilon - Simplification tolerance (larger = more simplification)
 * @returns Simplified polygon vertices
 */
export function simplifyPolygon(points: Point2D[], epsilon: number): Point2D[] {
  if (points.length <= 3 || epsilon <= 0) return [...points];

  // For a closed polygon, run RDP on a doubled array and take unique points
  const doubled = [...points, ...points];
  const simplified = rdpSimplify(doubled, epsilon * epsilon);

  // Remove duplicates and ensure we don't have more than original
  const unique: Point2D[] = [];
  for (const p of simplified) {
    if (unique.length === 0 || (unique[unique.length - 1].x !== p.x || unique[unique.length - 1].y !== p.y)) {
      unique.push(p);
    }
  }

  // Close the polygon check
  if (unique.length > 1 && unique[0].x === unique[unique.length - 1].x && unique[0].y === unique[unique.length - 1].y) {
    unique.pop();
  }

  return unique.length >= 3 ? unique : points;
}

/**
 * Ramer-Douglas-Peucker simplification (recursive).
 */
function rdpSimplify(points: Point2D[], epsilonSq: number): Point2D[] {
  if (points.length < 3) return [...points];

  // Find the point with the maximum distance from the line between first and last
  const first = points[0];
  const last = points[points.length - 1];
  let maxDistSq = 0;
  let maxIndex = 0;

  for (let i = 1; i < points.length - 1; i++) {
    const distSq = pointToSegmentDistanceSq(points[i], first, last);
    if (distSq > maxDistSq) {
      maxDistSq = distSq;
      maxIndex = i;
    }
  }

  // If max distance is greater than epsilon, recursively simplify
  if (maxDistSq > epsilonSq) {
    const left = rdpSimplify(points.slice(0, maxIndex + 1), epsilonSq);
    const right = rdpSimplify(points.slice(maxIndex), epsilonSq);
    return left.slice(0, -1).concat(right);
  } else {
    return [first, last];
  }
}

/**
 * Reduce hull to a maximum number of vertices by removing
 * the vertices that contribute least to the shape.
 *
 * @param hull - Convex hull vertices (CCW)
 * @param maxVertices - Maximum number of vertices to keep
 * @returns Reduced hull (still CCW, still convex)
 */
export function reduceHullVertices(hull: Point2D[], maxVertices: number): Point2D[] {
  if (hull.length <= maxVertices) return hull;

  let current = [...hull];

  while (current.length > maxVertices) {
    // Find the vertex with minimum "importance" (area contribution)
    let minImportance = Infinity;
    let minIndex = -1;

    for (let i = 0; i < current.length; i++) {
      const prev = current[(i - 1 + current.length) % current.length];
      const curr = current[i];
      const next = current[(i + 1) % current.length];

      // Importance = area of triangle formed by prev, curr, next
      const importance = Math.abs(cross(prev, curr, next));
      if (importance < minImportance) {
        minImportance = importance;
        minIndex = i;
      }
    }

    // Remove the least important vertex
    if (minIndex >= 0) {
      current.splice(minIndex, 1);
    } else {
      break;
    }
  }

  return current;
}

// ============================================================================
// IMAGE TO HULL CONVERSION
// ============================================================================

/**
 * Extract outline points from an image's alpha channel.
 *
 * @param imageData - ImageData from canvas (RGBA)
 * @param alphaThreshold - Pixels with alpha > threshold are considered solid
 * @param scale - Scale factor to convert pixels to world units
 * @param pivotX - Pivot X (0-1, 0.5 = center)
 * @param pivotY - Pivot Y (0-1, 0.5 = center)
 * @returns Array of edge points in local space
 */
export function extractSpriteOutline(
  imageData: ImageData,
  alphaThreshold: number,
  scale: number = 1,
  pivotX: number = 0.5,
  pivotY: number = 0.5,
): Point2D[] {
  const { width, height, data } = imageData;
  const points: Point2D[] = [];

  // Create binary mask
  const isSolid = (x: number, y: number): boolean => {
    if (x < 0 || x >= width || y < 0 || y >= height) return false;
    const idx = (y * width + x) * 4;
    return data[idx + 3] > alphaThreshold;
  };

  // Find edge pixels (solid pixels adjacent to non-solid)
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (!isSolid(x, y)) continue;

      // Check if this is an edge pixel
      const isEdge =
        !isSolid(x - 1, y) || !isSolid(x + 1, y) ||
        !isSolid(x, y - 1) || !isSolid(x, y + 1) ||
        !isSolid(x - 1, y - 1) || !isSolid(x + 1, y - 1) ||
        !isSolid(x - 1, y + 1) || !isSolid(x + 1, y + 1);

      if (isEdge) {
        // Convert to local space with pivot offset
        // Note: Y is flipped (image Y goes down, world Y goes up)
        const px = (x + 0.5 - width * pivotX) * scale;
        const py = (height * pivotY - y - 0.5) * scale;
        points.push({ x: px, y: py });
      }
    }
  }

  return points;
}

/**
 * Generate a convex collider from sprite image data.
 *
 * @param imageData - ImageData from canvas (RGBA)
 * @param options - Generation options
 * @returns Convex hull points in local space (CCW winding)
 */
export function generateConvexFromSprite(
  imageData: ImageData,
  options: {
    alphaThreshold?: number;
    simplification?: number;
    maxVertices?: number;
    scale?: number;
    pivotX?: number;
    pivotY?: number;
  } = {},
): Point2D[] {
  const {
    alphaThreshold = 128,
    simplification = 1,
    maxVertices = 8,
    scale = 1,
    pivotX = 0.5,
    pivotY = 0.5,
  } = options;

  // Extract edge points
  const outline = extractSpriteOutline(imageData, alphaThreshold, scale, pivotX, pivotY);

  if (outline.length < 3) {
    // Return a default unit square if no valid outline
    return [
      { x: -0.5 * scale, y: -0.5 * scale },
      { x: 0.5 * scale, y: -0.5 * scale },
      { x: 0.5 * scale, y: 0.5 * scale },
      { x: -0.5 * scale, y: 0.5 * scale },
    ];
  }

  // Compute convex hull
  let hull = computeConvexHull(outline);

  // Simplify if requested
  if (simplification > 0 && hull.length > maxVertices) {
    hull = simplifyPolygon(hull, simplification * scale * 0.01);
  }

  // Reduce to max vertices
  hull = reduceHullVertices(hull, maxVertices);

  // Ensure minimum 3 vertices
  if (hull.length < 3) {
    return [
      { x: -0.5 * scale, y: -0.5 * scale },
      { x: 0.5 * scale, y: -0.5 * scale },
      { x: 0.5 * scale, y: 0.5 * scale },
      { x: -0.5 * scale, y: 0.5 * scale },
    ];
  }

  return hull;
}

/**
 * Ensure hull is wound counter-clockwise.
 */
export function ensureCCW(hull: Point2D[]): Point2D[] {
  if (hull.length < 3) return hull;

  // Calculate signed area (positive = CCW, negative = CW)
  let area = 0;
  for (let i = 0; i < hull.length; i++) {
    const j = (i + 1) % hull.length;
    area += hull[i].x * hull[j].y;
    area -= hull[j].x * hull[i].y;
  }

  // If clockwise, reverse
  if (area < 0) {
    return [...hull].reverse();
  }

  return hull;
}

// ============================================================================
// SHAPE DETECTION
// ============================================================================

export type DetectedShapeType = 'polygon' | 'box' | 'circle';

export interface DetectedShape {
  type: DetectedShapeType;
  /** For polygon: the hull points */
  points: Point2D[];
  /** For box: half extents */
  boxHalfExtents?: { x: number; y: number };
  /** For circle: radius */
  circleRadius?: number;
  /** For circle/box: center offset from hull centroid */
  centerOffset?: { x: number; y: number };
}

/**
 * Compute the centroid of a polygon.
 */
function computeCentroid(points: Point2D[]): Point2D {
  let cx = 0, cy = 0;
  for (const p of points) {
    cx += p.x;
    cy += p.y;
  }
  return { x: cx / points.length, y: cy / points.length };
}

/**
 * Compute the axis-aligned bounding box of points.
 */
function computeAABB(points: Point2D[]): { min: Point2D; max: Point2D } {
  let minX = Infinity, minY = Infinity;
  let maxX = -Infinity, maxY = -Infinity;
  for (const p of points) {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  }
  return { min: { x: minX, y: minY }, max: { x: maxX, y: maxY } };
}

/**
 * Compute the area of a convex polygon.
 */
function computePolygonArea(points: Point2D[]): number {
  let area = 0;
  for (let i = 0; i < points.length; i++) {
    const j = (i + 1) % points.length;
    area += points[i].x * points[j].y;
    area -= points[j].x * points[i].y;
  }
  return Math.abs(area) / 2;
}

/**
 * Check if a hull is approximately a box (axis-aligned rectangle).
 * Returns confidence score (0-1), where 1 = perfect box.
 */
function checkBoxFit(hull: Point2D[]): { fit: number; halfExtents: { x: number; y: number }; center: Point2D } {
  const aabb = computeAABB(hull);
  const width = aabb.max.x - aabb.min.x;
  const height = aabb.max.y - aabb.min.y;
  const boxArea = width * height;
  const hullArea = computePolygonArea(hull);
  
  // Perfect box has area ratio = 1
  const areaRatio = hullArea / boxArea;
  
  // Check if vertices are close to corners
  const cx = (aabb.min.x + aabb.max.x) / 2;
  const cy = (aabb.min.y + aabb.max.y) / 2;
  const halfW = width / 2;
  const halfH = height / 2;
  
  // Expected corners
  const corners = [
    { x: cx - halfW, y: cy - halfH },
    { x: cx + halfW, y: cy - halfH },
    { x: cx + halfW, y: cy + halfH },
    { x: cx - halfW, y: cy + halfH },
  ];
  
  // Compute how well hull vertices match corners
  let cornerScore = 0;
  if (hull.length === 4) {
    for (const hv of hull) {
      let minDist = Infinity;
      for (const c of corners) {
        const dx = hv.x - c.x;
        const dy = hv.y - c.y;
        minDist = Math.min(minDist, Math.sqrt(dx * dx + dy * dy));
      }
      // Normalize by diagonal
      const diag = Math.sqrt(width * width + height * height);
      cornerScore += 1 - Math.min(1, minDist / (diag * 0.1));
    }
    cornerScore /= 4;
  }
  
  // Combined fit score (area ratio is most important)
  const fit = hull.length === 4 ? (areaRatio * 0.6 + cornerScore * 0.4) : areaRatio * 0.5;
  
  return {
    fit,
    halfExtents: { x: halfW, y: halfH },
    center: { x: cx, y: cy },
  };
}

/**
 * Check if a hull is approximately a circle.
 * Returns confidence score (0-1), where 1 = perfect circle.
 */
function checkCircleFit(hull: Point2D[]): { fit: number; radius: number; center: Point2D } {
  const center = computeCentroid(hull);
  
  // Compute distances from center to all vertices
  let sumDist = 0;
  let minDist = Infinity;
  let maxDist = 0;
  
  for (const p of hull) {
    const dx = p.x - center.x;
    const dy = p.y - center.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    sumDist += dist;
    minDist = Math.min(minDist, dist);
    maxDist = Math.max(maxDist, dist);
  }
  
  const avgRadius = sumDist / hull.length;
  
  // Circle fit = how uniform are the distances
  // Perfect circle: minDist == maxDist
  const variance = (maxDist - minDist) / avgRadius;
  const fit = Math.max(0, 1 - variance * 2);
  
  // Also check area ratio (circle vs hull)
  const circleArea = Math.PI * avgRadius * avgRadius;
  const hullArea = computePolygonArea(hull);
  const areaFit = Math.min(hullArea / circleArea, circleArea / hullArea);
  
  // Need enough vertices for a good circle approximation
  const vertexPenalty = hull.length < 6 ? 0.5 : 1;
  
  return {
    fit: fit * 0.5 + areaFit * 0.5 * vertexPenalty,
    radius: avgRadius,
    center,
  };
}

/**
 * Detect the optimal shape type for a convex hull.
 * Returns the best fitting primitive (box, circle) or falls back to polygon.
 *
 * @param hull - Convex hull points (CCW)
 * @param boxThreshold - Minimum fit score to use box (default: 0.92)
 * @param circleThreshold - Minimum fit score to use circle (default: 0.90)
 */
export function detectOptimalShape(
  hull: Point2D[],
  boxThreshold: number = 0.92,
  circleThreshold: number = 0.90,
): DetectedShape {
  if (hull.length < 3) {
    return { type: 'polygon', points: hull };
  }

  const boxCheck = checkBoxFit(hull);
  const circleCheck = checkCircleFit(hull);
  
  // Prefer box if it's a good fit (simpler collision)
  if (boxCheck.fit >= boxThreshold && boxCheck.fit >= circleCheck.fit) {
    return {
      type: 'box',
      points: hull,
      boxHalfExtents: boxCheck.halfExtents,
      centerOffset: boxCheck.center,
    };
  }
  
  // Then try circle
  if (circleCheck.fit >= circleThreshold) {
    return {
      type: 'circle',
      points: hull,
      circleRadius: circleCheck.radius,
      centerOffset: circleCheck.center,
    };
  }
  
  // Fall back to polygon
  return { type: 'polygon', points: hull };
}

/**
 * Generate an optimal convex collider from sprite image data.
 * Automatically detects if box or circle would be a better fit.
 *
 * @param imageData - ImageData from canvas (RGBA)
 * @param options - Generation options
 * @returns Detected shape with optimal type
 */
export function generateOptimalCollider(
  imageData: ImageData,
  options: {
    alphaThreshold?: number;
    simplification?: number;
    maxVertices?: number;
    scale?: number;
    pivotX?: number;
    pivotY?: number;
    boxThreshold?: number;
    circleThreshold?: number;
  } = {},
): DetectedShape {
  const {
    alphaThreshold = 128,
    simplification = 1,
    maxVertices = 0, // 0 = no limit
    scale = 1,
    pivotX = 0.5,
    pivotY = 0.5,
    boxThreshold = 0.92,
    circleThreshold = 0.90,
  } = options;

  // Extract edge points
  const outline = extractSpriteOutline(imageData, alphaThreshold, scale, pivotX, pivotY);

  if (outline.length < 3) {
    // Return a default unit square if no valid outline
    return {
      type: 'box',
      points: [
        { x: -0.5 * scale, y: -0.5 * scale },
        { x: 0.5 * scale, y: -0.5 * scale },
        { x: 0.5 * scale, y: 0.5 * scale },
        { x: -0.5 * scale, y: 0.5 * scale },
      ],
      boxHalfExtents: { x: 0.5 * scale, y: 0.5 * scale },
      centerOffset: { x: 0, y: 0 },
    };
  }

  // Compute convex hull
  let hull = computeConvexHull(outline);

  // Simplify if requested
  if (simplification > 0) {
    hull = simplifyPolygon(hull, simplification * scale * 0.01);
  }

  // Reduce to max vertices if specified
  if (maxVertices > 0 && hull.length > maxVertices) {
    hull = reduceHullVertices(hull, maxVertices);
  }

  // Ensure minimum 3 vertices
  if (hull.length < 3) {
    return {
      type: 'box',
      points: [
        { x: -0.5 * scale, y: -0.5 * scale },
        { x: 0.5 * scale, y: -0.5 * scale },
        { x: 0.5 * scale, y: 0.5 * scale },
        { x: -0.5 * scale, y: 0.5 * scale },
      ],
      boxHalfExtents: { x: 0.5 * scale, y: 0.5 * scale },
      centerOffset: { x: 0, y: 0 },
    };
  }

  // Detect optimal shape
  return detectOptimalShape(hull, boxThreshold, circleThreshold);
}
